import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { isColumnsComponent, isColumnComponent } from './utils/SmartLayoutUtils.js';
import { Tooltip } from 'primereact/tooltip';
import { SpeedDial } from 'primereact/speeddial';
import styles from './DesignActionsContainer.module.scss.js';
import { classNames } from 'primereact/utils';
import { useResizeListener, useOverlayScrollListener } from 'primereact/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faObjectUngroup, faCopy, faTrashCan, faPlus, faArrowsUpDownLeftRight } from '@fortawesome/pro-solid-svg-icons';

const CloneButton = (props) => {
    const { handleClick, meta } = props;
    return (React.createElement("div", { onClick: () => handleClick(meta), className: "text-white cursor-pointer relative p-1", style: { width: "1.5rem", height: "1.5rem", display: "grid", placeItems: "center" } },
        React.createElement(FontAwesomeIcon, { icon: faCopy, style: {
                fontWeight: 400,
                fontSize: ".875rem",
                lineHeight: "0.825rem"
            } })));
};
const DeleteButton = (props) => {
    const { handleClick, meta } = props;
    return (React.createElement("div", { onClick: () => handleClick(meta), className: "text-white cursor-pointer relative p-1", style: { width: "1.5rem", height: "1.5rem", display: "grid", placeItems: "center" } },
        React.createElement(FontAwesomeIcon, { icon: faTrashCan, style: {
                fontWeight: 400,
                fontSize: ".875rem",
                lineHeight: "0.825rem"
            } })));
};
const ColumnsOptions = (props) => {
    const { handleClick, meta } = props;
    const items = [
        {
            label: 'Add Column to Beginning',
            icon: 'c2-icons c2-icons-justify-start',
            className: "add-col-beg",
            command: () => {
                handleClick({ type: "addBeginning", meta });
            }
        },
        {
            label: 'Add Column to End',
            icon: 'c2-icons c2-icons-justify-end',
            className: "add-col-end",
            command: () => {
                handleClick({ type: "addLast", meta });
            }
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(Tooltip, { target: ".p-speeddial-action", position: "right" }),
        React.createElement(SpeedDial, { model: items, direction: "up", transitionDelay: 80, showIcon: "pi pi-cog", hideIcon: "pi pi-times", className: classNames(styles.columnsLayoutOptions), buttonTemplate: (btnOpts) => (React.createElement("div", Object.assign({}, btnOpts, { className: "text-white cursor-pointer relative p-1", style: { width: "1.5rem", height: "1.5rem", display: "grid", placeItems: "center" } }),
                React.createElement(FontAwesomeIcon, { icon: faPlus, style: {
                        fontWeight: 400,
                        fontSize: ".875rem",
                        lineHeight: "0.825rem"
                    } }))) })));
};
const ColumnOptions = (props) => {
    const { handleClick, meta } = props;
    const items = [
        {
            label: 'Add Column Before',
            icon: 'fa-kit fa-add-column-left-solid',
            className: "add-col-beg",
            command: () => {
                handleClick({ type: "addBefore", meta });
            }
        },
        {
            label: 'Add Column After',
            icon: 'fa-kit fa-add-column-right-solid',
            className: "add-col-end",
            command: () => {
                handleClick({ type: "addAfter", meta });
            }
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(Tooltip, { target: ".p-speeddial-action", position: "right" }),
        React.createElement(SpeedDial, { model: items, direction: "up", transitionDelay: 80, showIcon: React.createElement(FontAwesomeIcon, { icon: faPlus, style: {
                    fontWeight: 400,
                    fontSize: ".875rem",
                    lineHeight: "0.825rem"
                } }), hideIcon: React.createElement(FontAwesomeIcon, { icon: faPlus, style: {
                    fontWeight: 400,
                    fontSize: ".875rem",
                    lineHeight: "0.825rem"
                } }), className: classNames(styles.columnsLayoutOptions), pt: {
                menuitem: {
                    style: {}
                }
            }, buttonTemplate: (btnOpts) => (React.createElement("div", Object.assign({}, btnOpts, { className: "text-white cursor-pointer relative p-1", style: { width: "1.5rem", height: "1.5rem", display: "grid", placeItems: "center" } }),
                React.createElement(FontAwesomeIcon, { icon: faPlus, style: {
                        fontWeight: 400,
                        fontSize: ".875rem",
                        lineHeight: "0.825rem"
                    } }))) })));
};
const DragButton = (props) => {
    const { listeners, attributes } = props;
    return (React.createElement("div", Object.assign({}, listeners, attributes, { style: { padding: "4px 6px" }, className: "bg-indigo-100 cursor-pointer relative text-indigo-500 border-indigo-500 border-round-lg border-1" }),
        React.createElement(FontAwesomeIcon, { icon: faArrowsUpDownLeftRight, style: {
                fontWeight: 400,
                fontSize: "1rem",
                lineHeight: "0.825rem"
            } })));
};
const DesignActionsContainer = ({ listeners, attributes, meta, isRootComponent, onClone, onDelete, onUngroup, onColumnOperation, designMode, positions }) => {
    var _a, _b;
    const [position, setPosition] = useState({ left: 0, top: 0, display: "none" });
    const containerRef = useRef(null);
    const setFixedPosition = useCallback(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        ((_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().left) ? ((_d = (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.parentElement) === null || _d === void 0 ? void 0 : _d.getBoundingClientRect().left) - 2 : 0;
        ((_f = (_e = containerRef.current) === null || _e === void 0 ? void 0 : _e.parentElement) === null || _f === void 0 ? void 0 : _f.getBoundingClientRect().top) ? ((_h = (_g = containerRef.current) === null || _g === void 0 ? void 0 : _g.parentElement) === null || _h === void 0 ? void 0 : _h.getBoundingClientRect().top) - 40 : 0;
        setPosition({ left: "unset", top: "unset", display: "flex" });
    }, [containerRef.current, designMode]);
    const positionsOfContainer = useMemo(() => {
        var _a, _b;
        const widthOfDesignActionContainer = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width;
        if (positions && widthOfDesignActionContainer)
            return {
                top: positions.top + 4,
                left: positions.left - (widthOfDesignActionContainer / 2)
            };
        return { top: "-34px" };
    }, [positions === null || positions === void 0 ? void 0 : positions.left, positions === null || positions === void 0 ? void 0 : positions.top, (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()]);
    const [bindWindowResizeListener, unbindWindowResizeListener] = useResizeListener({
        listener: setFixedPosition
    });
    const [bindOverlayScrollListener, unbindOverlayScrollListener] = useOverlayScrollListener({
        target: containerRef,
        listener: setFixedPosition,
        options: { passive: true },
        when: position.display === "flex"
    });
    useEffect(() => {
        bindWindowResizeListener();
        bindOverlayScrollListener();
        return () => {
            unbindWindowResizeListener();
            unbindOverlayScrollListener();
        };
    }, [bindWindowResizeListener, unbindWindowResizeListener]);
    useEffect(() => {
        setFixedPosition();
    }, [setFixedPosition]);
    return (React.createElement("div", { className: "absolute", style: positionsOfContainer, ref: containerRef },
        React.createElement("div", { className: "fixed align-items-center gap-1 z-2", style: position },
            React.createElement("div", { className: "flex bg-white-500 border-1 border-white-600", style: { borderRadius: ".5rem", gap: ".5rem", padding: "0.125rem 0.5rem 0.125rem 0.5rem" } },
                React.createElement("div", { className: "white-space-nowrap font-normal text-sm text-white line-height-2 align-self-center" }, meta.name),
                (meta.cloneable && !isRootComponent) && (React.createElement(CloneButton, { meta: meta, handleClick: onClone })),
                isColumnsComponent(meta) && (React.createElement(ColumnsOptions, { meta: meta, handleClick: (e) => onColumnOperation(e) })),
                isColumnComponent(meta) && (React.createElement(ColumnOptions, { meta: meta, handleClick: (e) => onColumnOperation(e) })),
                (meta.deletable !== false && !isRootComponent) && (React.createElement(DeleteButton, { meta: meta, handleClick: onDelete })),
                meta.props.isGroup && (React.createElement("div", { onClick: () => onUngroup && onUngroup(meta), className: "text-white cursor-pointer text-sm relative p-1", style: { display: "grid", placeItems: "center" } },
                    React.createElement(Tooltip, { target: ".ungroup-icon" }),
                    React.createElement("span", { className: "ungroup-icon", "data-pr-tooltip": "Ungroup" },
                        React.createElement(FontAwesomeIcon, { icon: faObjectUngroup, style: {
                                fontWeight: 400,
                                fontSize: "1rem",
                                lineHeight: "0.825rem"
                            } }))))),
            React.createElement("div", { className: "flex" }, (meta.draggable !== false && !isRootComponent) && designMode !== "basic" && React.createElement(DragButton, { listeners: listeners, attributes: attributes })))));
};

export { ColumnOptions, ColumnsOptions, DeleteButton, DesignActionsContainer, DragButton };
//# sourceMappingURL=DesignActionsContainer.js.map
